import React, { FC, useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import Icon from "@icon-park/react/es/all";
import styled from "styled-components";
import {
  ColorType,
  Text,
  theme,
  TextTypeAndSize,
} from "@ifgengineering/component-library";

interface SimpleTooltipProps {
  text: string | React.ReactElement;
  type?: TextTypeAndSize;
  color?: ColorType;
  background?: ColorType;
  icon?: string;
  iconColor?: ColorType;
  trigger?: "hover" | "click";
}

const Container = styled.div`
  position: relative;
  display: flex;
`;

const OverArea = styled.div<{ trigger?: "hover" | "click" }>`
  display: inline-flex;
  cursor: ${({ trigger }) =>
    trigger === "hover" ? "context-menu" : "pointer"};
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
`;

export const CloseButton = styled(Icon)`
  position: absolute;
  cursor: pointer;
  top: 0.75rem;
  right: 0.75rem;
`;

const Tooltip = styled.div<{
  color?: ColorType;
  background?: ColorType;
}>`
  position: fixed;
  padding: 16px;
  background-color: ${({ background }) =>
    background ? theme.colors[background] : theme.colors.GOLD100};
  border-radius: 8px;
  box-shadow: 0 0px 10px ${theme.colors.SLATE300};
  z-index: 9999;
  width: 300px;

  &::before {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 10px 10px 0 10px;
    border-color: ${({ background }) =>
        background ? theme.colors[background] : theme.colors.GOLD100}
      transparent transparent transparent;
  }
`;

const SimpleTooltip: FC<SimpleTooltipProps> = ({
  text,
  type = "P14",
  color = "SLATE700",
  background,
  icon = "Info",
  iconColor = "SLATE500",
  trigger = "hover",
}) => {
  const [show, setShow] = useState(false);
  const triggerRef = useRef<HTMLButtonElement & HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 10,
        left: rect.left + rect.width / 2,
      });
    }
  }, []);

  useEffect(() => {
    if (!show) return;

    updatePosition();

    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, {
      capture: true,
      passive: true,
    });

    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, {
        capture: true,
      });
    };
  }, [show, updatePosition]);

  const triggerActions =
    trigger === "hover"
      ? {
          onMouseEnter: () => setShow(true),
          onMouseLeave: () => setShow(false),
        }
      : {
          onClick: () => setShow(!show),
        };

  const tooltipElement = (
    <Tooltip
      background={background}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: "translate(-50%, -100%)",
      }}
    >
      {trigger === "click" && (
        <CloseButton
          type="Close"
          fill={theme.colors[iconColor]}
          onClick={() => setShow(!show)}
        />
      )}
      <Text type={type} color={color}>
        {text}
      </Text>
    </Tooltip>
  );

  return (
    <Container>
      <OverArea
        as={trigger === "click" ? "button" : "div"}
        ref={triggerRef}
        trigger={trigger}
        {...triggerActions}
        {...(trigger === "click" && {
          type: "button",
          "aria-haspopup": "true",
          "aria-expanded": show,
        })}
      >
        <Icon type={icon} fill={theme.colors[iconColor]} />
      </OverArea>
      {show &&
        (typeof document !== "undefined"
          ? createPortal(tooltipElement, document.body)
          : tooltipElement)}
    </Container>
  );
};

export default SimpleTooltip;
